/**
 * @fileoverview `<QrCode>` — rendu QR code scannable.
 *
 * Sert notamment à l'enrôlement MFA TOTP (`otpauth://...`) pour Google
 * Authenticator et applications compatibles. Le rendu SVG reste net sur
 * écran dense et à l'impression.
 */

import { QRCodeSVG } from 'qrcode.react';
import { cn } from '../lib/utils.ts';

export interface QrCodeProps {
  /** Valeur encodée dans le QR code. Ex : URI `otpauth://totp/...`. */
  value: string;
  /** Taille du QR en pixels CSS. Défaut : 168. */
  size?: number;
  /** Niveau de correction d'erreur QR. Défaut : `M`. */
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  /** Libellé visible optionnel. */
  label?: string;
  /** Texte secondaire optionnel. */
  description?: string;
  /** Classe sur le conteneur racine. */
  className?: string;
}

/**
 * QR code GNPC.
 *
 * @param props - Valeur à encoder, taille SVG, niveau de correction d'erreur
 * et textes optionnels de légende.
 * @returns Figure contenant un QR code SVG scannable et une légende optionnelle.
 *
 * @example
 * ```tsx
 * <QrCode value={otpauthUri} label="Scanner avec Google Authenticator" />
 * ```
 */
export function QrCode({
  value,
  size = 168,
  errorCorrectionLevel = 'M',
  label,
  description,
  className,
}: QrCodeProps): React.JSX.Element {
  return (
    <figure className={cn('inline-flex flex-col items-center gap-3', className)}>
      <div className="border-border rounded-lg border bg-white p-3">
        <QRCodeSVG
          value={value}
          size={size}
          level={errorCorrectionLevel}
          includeMargin={false}
          fgColor="#001540"
          bgColor="#ffffff"
          // Nom accessible du SVG (`role="img"`) : sans `<title>`, axe
          // signale `svg-img-alt`. On réutilise le libellé visible quand il
          // existe, sinon un intitulé générique décrivant l'objet.
          title={label ?? 'QR code à scanner'}
        />
      </div>
      {(label || description) && (
        <figcaption className="max-w-xs text-center">
          {label && <p className="text-quipux-navy-950 text-sm font-medium">{label}</p>}
          {description && <p className="text-muted-foreground mt-1 text-xs">{description}</p>}
        </figcaption>
      )}
    </figure>
  );
}
